import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import Swal from 'sweetalert2'
import {GET, POST} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {useNavigate} from 'react-router-dom'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {
  CustomerForm,
  CustomerFormData,
  validateCustomerForm,
  formatCustomerDataForAPI,
  createEmptyCustomerData,
} from './CustomerForm'
import {ReservationRoomStatus, getReservationStatusLabel, formatDateForInput} from './types'
import {getStatusColor, RoomStatus} from '../HotelRoom/types'

type RoomOption = {
  id: number
  roomNumber: string
  type: string
  capacity: number
  price: number
  status: RoomStatus
}

type CustomerOption = {
  id: number
  name: string
  email: string
  phone: string
  gender: string
  birthDate: string
}

type WizardStep = 'customer' | 'reservation' | 'confirmation'

// Interface para o DTO de criação de reserva
type CreateHotelReservationDto = {
  roomsId: number[]
  customerId: number
  password: string
  checkIn: string
  checkOut?: string
  status: string
  subscription: boolean
  additionalGuests: any[]
}

const CreateHotelReservationContent: FC = () => {
  const navigate = useNavigate()
  const {t} = i18n
  const [loading, setLoading] = useState(false)
  const [loadingCustomers, setLoadingCustomers] = useState(true)
  const [currentStep, setCurrentStep] = useState<WizardStep>('customer')
  const [rooms, setRooms] = useState<RoomOption[]>([])
  const [customers, setCustomers] = useState<CustomerOption[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null)
  const [customerSearchTerm, setCustomerSearchTerm] = useState('')
  const [isCreatingNewCustomer, setIsCreatingNewCustomer] = useState(false)

  // Dados do formulário de cliente
  const [customerData, setCustomerData] = useState<CustomerFormData>(createEmptyCustomerData)

  // Dados da reserva
  const [reservationData, setReservationData] = useState({
    roomIds: [] as string[], // Mudança para array de IDs dos quartos
    checkIn: '',
    checkOut: '',
    status: ReservationRoomStatus.PENDING,
    password: '',
    additionalGuests: [],
  })

  // Calcular quartos selecionados
  const selectedRooms = rooms.filter((room) => reservationData.roomIds.includes(room.id.toString()))

  const handleReservationInputChange = (field: string, value: string | ReservationRoomStatus) => {
    setReservationData({
      ...reservationData,
      [field]: value,
    })
  }

  // Função para manipular seleção de quartos
  const handleRoomSelection = (room: RoomOption) => {
    const currentIds = reservationData.roomIds
    let newIds: string[]

    if (room.status !== RoomStatus.AVAILABLE) {
      Swal.fire({
        icon: 'warning',
        title: 'Quarto indisponível',
        text: 'Este quarto não está disponível para reserva.',
      })
      return
    }

    if (currentIds.includes(room.id.toString())) {
      // Remove o quarto se já estiver selecionado
      newIds = currentIds.filter((id) => id !== room.id.toString())
    } else {
      // Adiciona o quarto se não estiver selecionado
      newIds = [...currentIds, room.id.toString()]
    }

    setReservationData({
      ...reservationData,
      roomIds: newIds,
    })
  }

  const validateCustomerFormStep = () => {
    if (isCreatingNewCustomer) {
      // Validar dados do novo cliente
      const validation = validateCustomerForm(customerData)
      if (!validation.isValid) {
        Swal.fire({
          icon: 'warning',
          title: 'Dados incompletos',
          text:
            validation.message || 'Por favor, preencha todos os campos obrigatórios do cliente.',
          confirmButtonText: 'OK',
        })
        return false
      }
      return true
    } else {
      if (!selectedCustomer) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Selecione um cliente!',
        })
        return false
      }
    }

    return true
  }

  const validateReservationForm = () => {
    if (!reservationData.roomIds || reservationData.roomIds.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Selecione pelo menos um quarto!',
      })
      return false
    }

    if (!reservationData.checkIn) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Data de check-in é obrigatória!',
      })
      return false
    }

    // Validar se check-in não é no passado
    const checkInDate = new Date(reservationData.checkIn)
    const now = new Date()
    if (checkInDate < now) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Data de check-in não pode ser no passado!',
      })
      return false
    }

    // Validar se check-out é depois do check-in
    if (reservationData.checkOut) {
      const checkOutDate = new Date(reservationData.checkOut)
      if (checkOutDate <= checkInDate) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Data de check-out deve ser posterior ao check-in!',
        })
        return false
      }
    }

    if (!reservationData.password || reservationData.password.length < 6) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Senha é obrigatória e deve ter pelo menos 6 dígitos!',
      })
      return false
    }

    return true
  }

  const getRooms = async () => {
    try {
      const res = await GET('/hotel-room')
      setRooms(res.data || [])
    } catch (e) {
      console.log('Erro ao carregar quartos:', e)
      setRooms([])
    }
  }

  const getCustomers = async () => {
    try {
      const res = await GET('/customer')

      const data = res.data.map((e: any) => {
        return {
          ...e,
          phone: e.customerPhones[0]?.numberPhone
            ? e.customerPhones[0].numberPhone.replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3')
            : '',
        }
      })

      setCustomers(data || [])
      setLoadingCustomers(false)
    } catch (e) {
      console.log('Erro ao carregar clientes:', e)
      setCustomers([])
      setLoadingCustomers(false)
    }
  }

  const createCustomerAndReservation = async () => {
    try {
      setLoading(true)

      let customerId: number

      if (isCreatingNewCustomer) {
        // Criar novo cliente usando a função de formatação
        const customerPayload = formatCustomerDataForAPI(customerData)

        const customerResponse = await POST('/customer', customerPayload)

        if (!customerResponse.success) {
          throw new Error(customerResponse.message || 'Erro ao criar cliente')
        }

        customerId = customerResponse.dados.id
          ? customerResponse.dados.id
          : customerResponse.dados.dados.id
          ? customerResponse.dados.dados.id
          : 0
      } else {
        customerId = selectedCustomer!.id
      }

      // Criar reservas para múltiplos quartos selecionados
      // Preparar dados conforme CreateHotelReservationDto
      const reservationPayload: CreateHotelReservationDto = {
        roomsId: selectedRooms.map((room) => room.id),
        customerId: customerId,
        password: reservationData.password,
        checkIn: new Date(reservationData.checkIn).toISOString(),
        checkOut: reservationData.checkOut
          ? new Date(reservationData.checkOut).toISOString()
          : undefined,
        status: reservationData.status,
        subscription: false, // TODO: Adicionar campo se necessário
        additionalGuests: reservationData.additionalGuests,
      }

      console.log('Criando reserva de hotel:', {
        rooms: selectedRooms.map((r) => r.roomNumber).join(', '),
        customerId,
        checkIn: new Date(reservationData.checkIn).toISOString(),
      })

      // Enviar para API
      const response = await POST('hotel-reservation', reservationPayload)

      if (!response.success) {
        throw new Error(response.message || 'Erro ao criar a reserva')
      }

      Swal.fire({
        icon: 'success',
        title: 'Reserva criada',
        text: `Reserva criada com sucesso para ${selectedRooms.length} quarto(s): ${selectedRooms
          .map((r) => r.roomNumber)
          .join(', ')}!`,
      }).then(() => {
        navigate('/hospitality/hotel-reservation')
      })

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: e || 'Erro ao criar a reserva!',
      })
      console.log(e)
    }
  }

  const handleNextStep = () => {
    if (currentStep === 'customer') {
      if (validateCustomerFormStep()) {
        setCurrentStep('reservation')
      }
    } else if (currentStep === 'reservation') {
      if (validateReservationForm()) {
        setCurrentStep('confirmation')
      }
    }
  }

  const handlePreviousStep = () => {
    if (currentStep === 'reservation') {
      setCurrentStep('customer')
    } else if (currentStep === 'confirmation') {
      setCurrentStep('reservation')
    }
  }

  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
      customer.phone.includes(customerSearchTerm)
  )

  const generatePassword = (len: number) => {
    const pwd = Array.from({length: len}, () => Math.floor(Math.random() * 10).toString()).join('')
    handleReservationInputChange('password', pwd)
  }

  useEffect(() => {
    getRooms()
    getCustomers()
  }, [])

  const renderWizardHeader = () => (
    <div className='d-flex justify-content-between mb-8'>
      <div
        className={`d-flex align-items-center ${
          currentStep === 'customer'
            ? 'text-primary'
            : currentStep === 'reservation' || currentStep === 'confirmation'
            ? 'text-success'
            : 'text-muted'
        }`}
      >
        <div
          className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${
            currentStep === 'customer'
              ? 'bg-primary text-white'
              : currentStep === 'reservation' || currentStep === 'confirmation'
              ? 'bg-success text-white'
              : 'bg-light text-muted'
          }`}
          style={{width: '40px', height: '40px'}}
        >
          <i className='fas fa-user'></i>
        </div>
        <div>
          <h6 className='mb-0'>{t('hospitality_room_reservation.create.wizard_step1')}</h6>
          <small>{t('hospitality_room_reservation.create.wizard_step_customer')}</small>
        </div>
      </div>

      <div
        className={`flex-grow-1 align-self-center mx-4 ${
          currentStep === 'reservation' || currentStep === 'confirmation'
            ? 'bg-success'
            : 'bg-light'
        }`}
        style={{height: '2px'}}
      ></div>

      <div
        className={`d-flex align-items-center ${
          currentStep === 'reservation'
            ? 'text-primary'
            : currentStep === 'confirmation'
            ? 'text-success'
            : 'text-muted'
        }`}
      >
        <div
          className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${
            currentStep === 'reservation'
              ? 'bg-primary text-white'
              : currentStep === 'confirmation'
              ? 'bg-success text-white'
              : 'bg-light text-muted'
          }`}
          style={{width: '40px', height: '40px'}}
        >
          <i className='fas fa-bed'></i>
        </div>
        <div>
          <h6 className='mb-0'>{t('hospitality_room_reservation.create.wizard_step2')}</h6>
          <small>{t('hospitality_room_reservation.create.wizard_step_reservation')}</small>
        </div>
      </div>

      <div
        className={`flex-grow-1 align-self-center mx-4 ${
          currentStep === 'confirmation' ? 'bg-success' : 'bg-light'
        }`}
        style={{height: '2px'}}
      ></div>

      <div
        className={`d-flex align-items-center ${
          currentStep === 'confirmation' ? 'text-primary' : 'text-muted'
        }`}
      >
        <div
          className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${
            currentStep === 'confirmation' ? 'bg-primary text-white' : 'bg-light text-muted'
          }`}
          style={{width: '40px', height: '40px'}}
        >
          <i className='fas fa-check'></i>
        </div>
        <div>
          <h6 className='mb-0'>{t('hospitality_room_reservation.create.wizard_step3')}</h6>
          <small>{t('hospitality_room_reservation.create.wizard_step_confirmation')}</small>
        </div>
      </div>
    </div>
  )

  const renderCustomerStep = () => (
    <div>
      <div className='mb-6'>
        <h3 className='mb-4'>{t('hospitality_room_reservation.create.wizard_customer_title')}</h3>
        <div className='row mb-6'>
          <div className='col-12'>
            <div className='card'>
              <div className='card-body'>
                <div className='d-flex gap-3 mb-4'>
                  <Button
                    text={t('hospitality_room_reservation.create.wizard_customer_search_button')}
                    color={!isCreatingNewCustomer ? 'primary' : 'light'}
                    size='small'
                    horizontalPadding={6}
                    btnAttr={{type: 'button'}}
                    click={() => setIsCreatingNewCustomer(false)}
                  />
                  <Button
                    text={t('hospitality_room_reservation.create.wizard_customer_new_button')}
                    color={isCreatingNewCustomer ? 'primary' : 'light'}
                    size='small'
                    horizontalPadding={6}
                    btnAttr={{type: 'button'}}
                    click={() => setIsCreatingNewCustomer(true)}
                  />
                </div>
                {!isCreatingNewCustomer ? (
                  loadingCustomers ? (
                    <div className='mt-5'>
                      <Loading />
                    </div>
                  ) : (
                    <div>
                      <div className='mb-4'>
                        <input
                          type='text'
                          className='form-control'
                          placeholder={t(
                            'hospitality_room_reservation.create.wizard_customer_search_placeholder'
                          )}
                          value={customerSearchTerm}
                          onChange={(e) => setCustomerSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className='row'>
                        {filteredCustomers.slice(0, 20).map((customer) => (
                          <div key={customer.id} className='col-md-6 mb-3'>
                            <div
                              className={`card cursor-pointer border-hover-primary ${
                                selectedCustomer?.id === customer.id
                                  ? 'border-primary bg-light-primary'
                                  : ''
                              }`}
                              onClick={() => setSelectedCustomer(customer)}
                            >
                              <div className='card-body p-4'>
                                <div className='d-flex align-items-center'>
                                  <div className='me-3'>
                                    <i className='fas fa-user-circle fs-2x text-primary'></i>
                                  </div>
                                  <div className='flex-grow-1'>
                                    <h6 className='mb-1'>{customer.name}</h6>
                                    <p className='text-muted mb-1 small'>{customer.email}</p>
                                    <p className='text-muted mb-0 small'>{customer.phone}</p>
                                  </div>
                                  {selectedCustomer?.id === customer.id && (
                                    <i className='fas fa-check-circle text-success fs-2x'></i>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {filteredCustomers.length > 20 && (
                        <div className='text-center py-2'>
                          <small className='text-muted'>
                            {t('hospitality_room_reservation.create.wizard_customer_showing_first')}
                          </small>
                        </div>
                      )}
                      {filteredCustomers.length === 0 && (
                        <div className='text-center py-4'>
                          <i className='fas fa-search fs-2x text-muted mb-3'></i>
                          <p className='text-muted'>
                            {t('hospitality_room_reservation.create.wizard_customer_none_found')}
                          </p>
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  <CustomerForm
                    customerData={customerData}
                    onCustomerChange={setCustomerData}
                    showTitle={false}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderReservationStep = () => (
    <div>
      <div className='mb-6'>
        <h3 className='mb-4'>
          {t('hospitality_room_reservation.create.wizard_reservation_title')}
        </h3>

        <div className='row mb-6'>
          <div className='col-12'>
            <div className='card'>
              <div className='card-body'>
                <div className='row'>
                  <div className='col-12'>
                    <label className='form-label fw-bold'>
                      {t('hospitality_room_reservation.create.wizard_reservation_room_label')}
                    </label>
                    <p className='text-muted mb-4'>
                      {t('hospitality_room_reservation.create.wizard_reservation_room_subtitle')}
                    </p>
                    <p>
                      <i className='fas fa-info-circle me-2'></i>
                      {t('hospitality_room_reservation.create.legend.title')}:
                      {Object.values(RoomStatus).map((status) => (
                        <span
                          key={status}
                          className={`badge border-${getStatusColor(status)} bg-${getStatusColor(
                            status
                          )} mx-1`}
                        >
                          {t(`hospitality_room_reservation.create.legend.${status}`)}
                        </span>
                      ))}
                    </p>

                    <div className='row overflow-auto' style={{maxHeight: '400px'}}>
                      {rooms.map((room) => (
                        <div key={room.id} className='col-2 mb-3'>
                          <div
                            className={`card cursor-pointer border-hover-primary ${
                              reservationData.roomIds.includes(room.id.toString())
                                ? 'border-primary bg-light-primary'
                                : `border-${getStatusColor(room.status)} bg-light-${getStatusColor(
                                    room.status
                                  )}`
                            }`}
                            onClick={() => handleRoomSelection(room)}
                          >
                            <div className='card-body p-4'>
                              <div className='d-flex align-items-center justify-content-between'>
                                <div className='d-flex align-items-center'>
                                  <div className='me-3'>
                                    <i className='fas fa-bed fs-2x text-primary'></i>
                                  </div>
                                  <div>
                                    <h6 className='mb-1'>{room.roomNumber}</h6>
                                    <p className='text-muted mb-1 small'>{room.type}</p>
                                    <p className='text-muted mb-0 small'>
                                      Cap: {room.capacity} | R$ {room.price.toFixed(2)}
                                    </p>
                                  </div>
                                </div>
                                {reservationData.roomIds.includes(room.id.toString()) && (
                                  <i className='fas fa-check-circle text-success fs-2x'></i>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {selectedRooms.length > 0 && (
                      <div className='alert alert-info mt-3'>
                        <strong>Quartos selecionados:</strong>{' '}
                        {selectedRooms.map((room) => room.roomNumber).join(', ')}
                        <br />
                        <strong>Total estimado:</strong> R${' '}
                        {selectedRooms.reduce((total, room) => total + room.price, 0).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
                <div className='row'>
                  <div className='col-md-6'>
                    <Input
                      label={t(
                        'hospitality_room_reservation.create.wizard_reservation_status_label'
                      )}
                      inputAttr={{
                        type: 'select',
                        placeholder: t(
                          'hospitality_room_reservation.create.wizard_reservation_status_placeholder'
                        ),
                      }}
                      value={reservationData.status}
                      change={(value: ReservationRoomStatus) =>
                        handleReservationInputChange('status', value)
                      }
                      options={Object.values(ReservationRoomStatus).map((status) => ({
                        value: status,
                        select: getReservationStatusLabel(status),
                      }))}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-6'>
                    <label className='form-label fw-normal'>
                      {t('hospitality_room_reservation.create.wizard_reservation_checkin_label')}
                    </label>
                    <input
                      className='form-control'
                      type='datetime-local'
                      value={reservationData.checkIn}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleReservationInputChange('checkIn', e.target.value)
                      }
                      min={formatDateForInput(new Date())}
                      required
                    />
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label fw-normal'>
                      {t('hospitality_room_reservation.create.wizard_reservation_checkout_label')}
                    </label>
                    <input
                      className='form-control'
                      type='datetime-local'
                      value={reservationData.checkOut}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleReservationInputChange('checkOut', e.target.value)
                      }
                      min={formatDateForInput(new Date())}
                      required
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-6 d-flex justify-content-between align-items-end gap-2'>
                    <Input
                      label={t(
                        'hospitality_room_reservation.create.wizard_reservation_password_label'
                      )}
                      inputAttr={{
                        type: 'password',
                        placeholder: t(
                          'hospitality_room_reservation.create.wizard_reservation_password_placeholder'
                        ),
                        maxLength: 6,
                        pattern: '[0-9]*',
                        inputMode: 'numeric',
                      }}
                      value={reservationData.password}
                      change={(value: string) => {
                        // Permite apenas números e até 6 caracteres
                        const numericValue = value.replace(/\D/g, '').slice(0, 6)
                        handleReservationInputChange('password', numericValue)
                      }}
                      disabled={loading}
                    />
                    <div className='d-flex justify-content-end align-items-end'>
                      <Button
                        text={t(
                          'hospitality_room_reservation.create.wizard_reservation_generate_password'
                        )}
                        color='primary'
                        size='small'
                        btnAttr={{
                          type: 'button',
                        }}
                        click={() => generatePassword(6)}
                        btnClass='fw-normal w-90px fs-8 px-0'
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderConfirmationStep = () => (
    <div>
      <h3 className='mb-4'>{t('hospitality_room_reservation.create.wizard_confirmation_title')}</h3>

      <div className='row'>
        <div className='col-md-6'>
          <div className='card bg-light-info mb-5'>
            <div className='card-body'>
              <h5 className='card-title'>
                <i className='fas fa-user me-2'></i>
                {t('hospitality_room_reservation.create.wizard_confirmation_customer_data')}
              </h5>
              {isCreatingNewCustomer ? (
                <div>
                  <p>
                    <strong>{t('hospitality_room_reservation.customer_form.name_label')}:</strong>{' '}
                    {customerData.name}
                  </p>
                  <p>
                    <strong>{t('hospitality_room_reservation.customer_form.email_label')}:</strong>{' '}
                    {customerData.email}
                  </p>
                  <p>
                    <strong>{t('hospitality_room_reservation.customer_form.cpf_label')}:</strong>{' '}
                    {customerData.cpf}
                  </p>
                  <p>
                    <strong>{t('hospitality_room_reservation.customer_form.phone_label')}:</strong>{' '}
                    {customerData.phoneNumber}
                  </p>
                  {customerData.gender && (
                    <p>
                      <strong>
                        {t('hospitality_room_reservation.customer_form.gender_label')}:
                      </strong>{' '}
                      {customerData.gender === 'masculino'
                        ? t('hospitality_room_reservation.create.wizard_confirmation_gender_male')
                        : customerData.gender === 'feminino'
                        ? t('hospitality_room_reservation.create.wizard_confirmation_gender_female')
                        : customerData.gender === 'outro'
                        ? t('hospitality_room_reservation.create.wizard_confirmation_gender_other')
                        : customerData.gender}
                    </p>
                  )}
                  {customerData.birthDate && (
                    <p>
                      <strong>
                        {t('hospitality_room_reservation.customer_form.birthdate_label')}:
                      </strong>{' '}
                      {new Date(customerData.birthDate).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                  <p>
                    <strong>
                      {t('hospitality_room_reservation.create.wizard_confirmation_address')}:
                    </strong>{' '}
                    {customerData.street}, {customerData.number} - {customerData.district} -{' '}
                    {customerData.city}/{customerData.state}
                  </p>
                  <p>
                    <strong>
                      {t('hospitality_room_reservation.create.wizard_confirmation_zipcode')}:
                    </strong>{' '}
                    {customerData.zipCode}
                  </p>
                </div>
              ) : selectedCustomer ? (
                <div>
                  <p>
                    <strong>{t('hospitality_room_reservation.customer_form.name_label')}:</strong>{' '}
                    {selectedCustomer.name}
                  </p>
                  <p>
                    <strong>{t('hospitality_room_reservation.customer_form.email_label')}:</strong>{' '}
                    {selectedCustomer.email}
                  </p>
                  <p>
                    <strong>{t('hospitality_room_reservation.customer_form.phone_label')}:</strong>{' '}
                    {selectedCustomer.phone}
                  </p>
                  <p>
                    <strong>{t('hospitality_room_reservation.customer_form.gender_label')}:</strong>{' '}
                    {selectedCustomer.gender === 'M'
                      ? t('hospitality_room_reservation.create.wizard_confirmation_gender_male')
                      : selectedCustomer.gender === 'F'
                      ? t('hospitality_room_reservation.create.wizard_confirmation_gender_female')
                      : selectedCustomer.gender === 'O'
                      ? t('hospitality_room_reservation.create.wizard_confirmation_gender_other')
                      : t(
                          'hospitality_room_reservation.create.wizard_confirmation_gender_not_informed'
                        )}
                  </p>
                  <p>
                    <strong>
                      {t('hospitality_room_reservation.customer_form.birthdate_label')}:
                    </strong>{' '}
                    {new Date(selectedCustomer.birthDate + `T12:00:00`).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className='col-md-6'>
          <div className='card bg-light-primary mb-5'>
            <div className='card-body'>
              <h5 className='card-title'>
                <i className='fas fa-bed me-2'></i>
                {t('hospitality_room_reservation.create.wizard_confirmation_reservation_data')}
              </h5>
              {selectedRooms.length > 0 && (
                <div>
                  <p>
                    <strong>
                      {t('hospitality_room_reservation.create.wizard_confirmation_room')}:
                    </strong>
                  </p>
                  {selectedRooms.map((room, index) => (
                    <div key={room.id} className='ps-3 mb-2'>
                      <p className='mb-1'>
                        • {room.roomNumber} ({room.type})
                      </p>
                      <p className='mb-1 text-muted small'>
                        {t('hospitality_room_reservation.create.wizard_confirmation_capacity')}:{' '}
                        {room.capacity} |
                        {t('hospitality_room_reservation.create.wizard_confirmation_price')}: R${' '}
                        {room.price.toFixed(2)}
                      </p>
                    </div>
                  ))}
                  <div className='mt-3 p-2 bg-light rounded'>
                    <p className='mb-1'>
                      <strong>Total de quartos:</strong> {selectedRooms.length}
                    </p>
                    <p className='mb-0'>
                      <strong>Total estimado:</strong> R${' '}
                      {selectedRooms.reduce((total, room) => total + room.price, 0).toFixed(2)}
                    </p>
                  </div>
                  <p>
                    <strong>
                      {t('hospitality_room_reservation.create.wizard_confirmation_checkin')}:
                    </strong>{' '}
                    {new Date(reservationData.checkIn).toLocaleString('pt-BR')}
                  </p>
                  {reservationData.checkOut && (
                    <p>
                      <strong>
                        {t('hospitality_room_reservation.create.wizard_confirmation_checkout')}:
                      </strong>{' '}
                      {new Date(reservationData.checkOut).toLocaleString('pt-BR')}
                    </p>
                  )}
                  <p>
                    <strong>
                      {t('hospitality_room_reservation.create.wizard_confirmation_status')}:
                    </strong>{' '}
                    {getReservationStatusLabel(reservationData.status)}
                  </p>
                  <p>
                    <strong>
                      {t('hospitality_room_reservation.create.wizard_confirmation_password')}:
                    </strong>{' '}
                    {reservationData.password}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {t('hospitality_room_reservation.create.wizard_page_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {t('hospitality_room_reservation.create.wizard_page_subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={t('hospitality_room_reservation.create.wizard_buttons_back')}
                  color='secondary'
                  iconColor='#FFF'
                  icon='fas fa-arrow-left'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/hotel-reservation')}
                  btnClass='mb-5 fw-bolder me-3'
                />
              </div>
            </div>
            <div className='card-body'>
              {renderWizardHeader()}

              <form>
                {currentStep === 'customer' && renderCustomerStep()}
                {currentStep === 'reservation' && renderReservationStep()}
                {currentStep === 'confirmation' && renderConfirmationStep()}

                <div className='d-flex justify-content-between mt-8'>
                  <div>
                    {currentStep !== 'customer' && (
                      <Button
                        text={t('hospitality_room_reservation.create.wizard_buttons_back')}
                        color='secondary'
                        size='small'
                        horizontalPadding={8}
                        btnAttr={{
                          type: 'button',
                        }}
                        click={handlePreviousStep}
                        btnClass='me-3'
                      />
                    )}
                  </div>

                  <div>
                    <Button
                      text={t('hospitality_room_reservation.create.wizard_buttons_cancel')}
                      color='danger'
                      size='small'
                      horizontalPadding={8}
                      btnAttr={{
                        type: 'button',
                      }}
                      click={() => navigate('/hospitality/hotel-reservation')}
                      btnClass='me-3'
                    />

                    {currentStep !== 'confirmation' ? (
                      <Button
                        text={t('hospitality_room_reservation.create.wizard_buttons_next')}
                        color='primary'
                        iconColor='#FFF'
                        icon='fas fa-arrow-right'
                        size='small'
                        horizontalPadding={8}
                        btnAttr={{
                          type: 'button',
                        }}
                        click={handleNextStep}
                      />
                    ) : (
                      <Button
                        text={t('hospitality_room_reservation.create.wizard_buttons_create')}
                        color='success'
                        iconColor='#FFF'
                        icon='fas fa-save'
                        size='small'
                        horizontalPadding={8}
                        btnAttr={{
                          type: 'button',
                        }}
                        click={createCustomerAndReservation}
                      />
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const CreateHotelReservation: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Reservas', path: 'hotel-reservation', isActive: false},
          {title: 'Nova Reserva', path: 'hotel-reservation/criar', isActive: true},
        ]}
      >
        Nova Reserva de Quarto
      </PageTitle>
      <CreateHotelReservationContent />
    </>
  )
}

export {CreateHotelReservation}
